import { currencyDisplay } from "../../../../utils/helpers";
import { Addon, FareType, MoneyFormatter, PaymentChannel, PayMode } from "./types";

function sumBy(items: Addon[], key: "amount" | "currency_amount") {
  return items.reduce((total, item) => total + Number(item?.[key] ?? 0), 0);
}

interface Selection {
  fareType: FareType;
  payMode: PayMode;
  selectedAddonIds: number[];
  selectedChannel: PaymentChannel | null;
}

const CARD_CHANNEL = "public_bank_2";

export const formatCcRate = (rate: number) => `${(rate * 100).toFixed(1)}%`;

// Mirrors the backend's .round(2) on the service fee.
const roundMoney = (amount: number) => Math.round(amount * 100) / 100;

// Every figure the dedicated payment page shows, derived from the new_payment
// payload and the customer's current selection.
//
// Unpaid add-ons are already part of `total_price` and `balance_amount`, so
// settling one reduces the remaining balance by exactly its amount.
export function usePaymentAmounts(paymentInfo: any, { fareType, payMode, selectedAddonIds, selectedChannel }: Selection) {
  const currency = paymentInfo?.currency ?? "MYR";
  const money: MoneyFormatter = (myr, foreign) =>
    currencyDisplay(currency, Number((currency === "MYR" ? myr : foreign) ?? 0));

  const totalPrice = paymentInfo?.total_price ?? 0;
  const currTotalPrice = paymentInfo?.currency_total_price ?? 0;
  const balanceAmount = paymentInfo?.balance_amount ?? 0;
  const currBalanceAmount = paymentInfo?.currency_balance_amount ?? 0;
  const deposit = paymentInfo?.deposit ?? 0;
  const currDeposit = paymentInfo?.currency_deposit ?? 0;
  const totalQuantity = paymentInfo?.total_quantity ?? 0;

  const addons: Addon[] = paymentInfo?.balance_revisions ?? [];
  const hasAddons = addons.length > 0;
  const selectableAddons = addons.filter((addon) => addon.payment_status !== "in_progress");
  const selectedAddons = addons.filter((addon) => selectedAddonIds.includes(addon.id));

  const addonsTotal = sumBy(addons, "amount");
  const currAddonsTotal = sumBy(addons, "currency_amount");
  const selectableTotal = sumBy(selectableAddons, "amount");
  const currSelectableTotal = sumBy(selectableAddons, "currency_amount");
  const selectedTotal = sumBy(selectedAddons, "amount");
  const currSelectedTotal = sumBy(selectedAddons, "currency_amount");

  const showDepositToggle = Boolean(paymentInfo?.allow_customer_pay_deposit && !paymentInfo?.deposit_covered);
  const payingDeposit = fareType === "customer_deposit" && showDepositToggle;
  const payingAddonsOnly = hasAddons && fareType === "customer_full_fare" && payMode === "selected_addons";

  // The design lists the tour fare on its own, with the unpaid add-ons broken
  // out beside it. It is the customer fare, so it includes any card fees
  // already paid; paid_amount includes them too, so they cancel out:
  // tour price + unpaid add-ons - paid to date = balance.
  const tourPrice = totalPrice - addonsTotal;
  const currTourPrice = currTotalPrice - currAddonsTotal;
  const paidToDate = paymentInfo?.paid_amount ?? 0;
  const currPaidToDate = paymentInfo?.currency_paid_amount ?? 0;
  const balanceExAddons = balanceAmount - addonsTotal;
  const currBalanceExAddons = currBalanceAmount - currAddonsTotal;

  const payingNow = payingAddonsOnly ? selectedTotal
    : fareType === "customer_deposit" ? deposit : balanceAmount;
  const currPayingNow = payingAddonsOnly ? currSelectedTotal
    : fareType === "customer_deposit" ? currDeposit : currBalanceAmount;

  // The card fee is charged on top of the amount paid now, so it never
  // reduces the balance. cc_rate already reflects any zero-CC-rate fair.
  // Card payments are MYR only (the SGD site never offers public_bank_2),
  // so there is no FX fee.
  const ccRate = Number(paymentInfo?.cc_rate ?? 0);
  const showAdminFee = selectedChannel?.value === CARD_CHANNEL && ccRate > 0;
  const adminFee = showAdminFee ? roundMoney(payingNow * ccRate) : 0;
  const totalDueNow = payingNow + adminFee;
  const currTotalDueNow = currPayingNow;

  const balanceAfterPayment = balanceAmount - payingNow;
  const currBalanceAfterPayment = currBalanceAmount - currPayingNow;

  const depositPerPax = totalQuantity > 0 ? deposit / totalQuantity : 0;
  const currDepositPerPax = totalQuantity > 0 ? currDeposit / totalQuantity : 0;
  // Balance left once the deposit being previewed is paid. `deposit` is what
  // is still owed of it, so earlier payments are already accounted for.
  const remainingAfterDeposit = balanceAmount - deposit;
  const currRemainingAfterDeposit = currBalanceAmount - currDeposit;

  return {
    currency,
    money,
    totalQuantity,

    addons,
    hasAddons,
    selectableAddons,
    addonsTotal,
    currAddonsTotal,
    selectableTotal,
    currSelectableTotal,

    tourPrice,
    currTourPrice,
    paidToDate,
    currPaidToDate,
    balanceAmount,
    currBalanceAmount,
    balanceExAddons,
    currBalanceExAddons,

    deposit,
    currDeposit,
    depositPerPax,
    currDepositPerPax,
    remainingAfterDeposit,
    currRemainingAfterDeposit,

    payingNow,
    currPayingNow,
    ccRate,
    showAdminFee,
    adminFee,
    totalDueNow,
    currTotalDueNow,
    balanceAfterPayment,
    currBalanceAfterPayment,

    showDepositToggle,
    payingDeposit,
    payingAddonsOnly,
    showPaymentTerms: Boolean(paymentInfo?.allow_customer_pay_deposit) || hasAddons,
    nothingSelected: payingAddonsOnly && selectedAddonIds.length === 0,
  };
}

export type PaymentAmounts = ReturnType<typeof usePaymentAmounts>;
